<script>
	import { onDestroy } from "svelte";
	import Peer from "simple-peer/simplepeer.min.js";
	import socket, { ME } from "../../api/socket.js";
	import { turn } from "../../api/api.js";
	import { distanceIntervaled } from "../../stores/common/intervaled.js";
	import talkie from "../../stores/talkie.js";
	import journal from "../../stores/journal.js";
	import journey from "../../stores/journey.js";
	import sound from "../../helpers/sound.js";
	import Display from "../windows/Display.svelte";
	import isMobile from "../../helpers/mobile.js";
	import t from "../../localization/t.js";

	const UPDATE_LOCATION_INTERVAL_METERS = 1000;
	const PEER_CONNECTION_TIMEOUT = 15000; // 15 seconds timeout for peer connections
	const STUN_SERVERS = [
		{ urls: "stun:stun.l.google.com:19302" },
		{ urls: "stun:stun1.l.google.com:19302" }
	];
	const PEER_RECONNECT_DELAY = 2000; // 2 seconds delay before reconnecting
	const MAX_PEER_RECONNECT_ATTEMPTS = 3;

	let turnServer = null;
	let turnExpires = 0;

	async function refreshTurnServer() {
		try {
			const { urls, username, credential, expires } = await turn();

			turnServer = { urls, username, credential };
			turnExpires = expires;
		} catch {
			turnServer = null;
		}
	}

	function iceServers() {
		// coturn rejects the credential once its timestamp passes, so a long-lived
		// session has to pick up a fresh one before peers get built.
		if (turnExpires - Math.floor(Date.now() / 1000) < 600) {
			refreshTurnServer();
		}

		return turnServer === null ? STUN_SERVERS : [...STUN_SERVERS, turnServer];
	}

	const location = distanceIntervaled(UPDATE_LOCATION_INTERVAL_METERS, ([$journey], set) => {
		if (!$journey.location) {
			return;
		}

		set({
			latitude: $journey.location.latitude,
			longitude: $journey.location.longitude
		});
	});

	let isOn = false;
	let isOnline = false;
	let noMic = false;
	let audioStream;
	let mediaStream;
	let peers = {};
	let peerTimeouts = {};
	let streams = {};
	let audioContext;
	let gain;
	let disposal = [];
	let wasTalking = false;
	let connectionErrors = {};
	let peerReconnectAttempts = {};
	let socketInitialized = false;
	let isDestroyed = false;

	$: inSound = isOn && sound("/talkie-in.wav");
	$: overSound = isOn && sound("/talkie-over.wav");
	$: peerCount = Object.keys(peers).length;

	$: displayText =
		"462 MHZ : " +
		(peerCount === 1
			? t("talkie.drivers_nearby_singular")
			: peerCount === 0
			? "no drivers nearby"
			: t("talkie.drivers_nearby_plural", { count: peerCount }));

	function createPeer(isInitiator, stream, identifier) {
		if (isDestroyed === true) {
			return;
		}

		// Clear any existing peer with this identifier
		if (peers[identifier]) {
			clearPeerConnection(identifier);
		}

		try {
			// Set timeout for peer connection
			peerTimeouts[identifier] = setTimeout(() => {
				if (peers[identifier] && !streams[identifier]) {
					connectionErrors[identifier] = "Connection timed out";
					destroyPeer(identifier);
				}
			}, PEER_CONNECTION_TIMEOUT);

			const peerOptions = {
				initiator: isInitiator,
				stream,
				trickle: true,
				config: {
					iceServers: iceServers()
				}
			};

			const peer = new Peer(peerOptions);
			peers[identifier] = peer;

			peer.on("signal", (signal) => {
				// Don't send signal if the peer is being destroyed
				if (!peers[identifier]) {
					return;
				}
				socket.send("client-signal", { signal }, identifier);
			});

			peer.on("stream", (stream) => {
				streams[identifier] = stream;
				clearTimeout(peerTimeouts[identifier]);
				delete connectionErrors[identifier];
			});

			peer.on("connect", () => {
				clearTimeout(peerTimeouts[identifier]);
				delete connectionErrors[identifier];
			});

			peer.on("error", (e) => {
				connectionErrors[identifier] = e.message;
				handlePeerFailure(identifier, isInitiator);
			});

			peer.on("iceConnectionStateChange", () => {
				const state = peer._pc?.iceConnectionState;

				if (state === "failed" || state === "disconnected") {
					handlePeerFailure(identifier, isInitiator);
				} else if (state === "connected" || state === "completed") {
					peerReconnectAttempts[identifier] = 0;
				}
			});

			peer.on("close", () => {
				handlePeerFailure(identifier, isInitiator);
			});
		} catch (error) {
			connectionErrors[identifier] = error.message;
			clearTimeout(peerTimeouts[identifier]);
		}
	}

	function clearPeerConnection(identifier) {
		const peer = peers[identifier];

		if (peer) {
			try {
				peer.removeAllListeners();
				peer.destroy();
			} catch (error) {
				// Ignore destroy errors
			}
		}

		if (peerTimeouts[identifier]) {
			clearTimeout(peerTimeouts[identifier]);
			delete peerTimeouts[identifier];
		}

		delete peers[identifier];

		const stream = streams[identifier];
		if (stream) {
			try {
				stream.getTracks().forEach((track) => track.stop());
			} catch (error) {
				// Ignore track stop errors
			}
			delete streams[identifier];
		}
	}

	function destroyPeer(identifier) {
		clearPeerConnection(identifier);
		delete peerReconnectAttempts[identifier];
		peers = { ...peers }; // Trigger reactivity
		streams = { ...streams }; // Trigger reactivity
	}

	function handlePeerFailure(identifier, isInitiator) {
		if (isDestroyed === true || isOn === false) {
			destroyPeer(identifier);
			return;
		}

		const attempts = peerReconnectAttempts[identifier] || 0;
		
		if (attempts >= MAX_PEER_RECONNECT_ATTEMPTS) {
			destroyPeer(identifier);
			return;
		}

		peerReconnectAttempts[identifier] = attempts + 1;
		clearPeerConnection(identifier);

		setTimeout(() => {
			if (isOn === true) {
				createPeer(isInitiator, mediaStream, identifier);
			}
		}, PEER_RECONNECT_DELAY * (attempts + 1));
	}

	async function createMediaStream(source) {
		try {
			if (audioContext === undefined || audioContext.state === "closed") {
				audioContext = new (window.AudioContext || window.webkitAudioContext)();
			}

			if (audioContext.state === "suspended") {
				await audioContext.resume();
			}

			const stream = new MediaStream();

			const microphone = audioContext.createMediaStreamSource(source);
			const destination = audioContext.createMediaStreamDestination();

			gain = audioContext.createGain();
			gain.gain.value = 0.0;

			microphone.connect(gain);
			gain.connect(destination);

			destination.stream.getAudioTracks().forEach((track) => {
				stream.addTrack(track);
			});

			return stream;
		} catch (error) {
			return null;
		}
	}

	function srcObject(node, stream) {
		if (!stream) return {};

		try {
			node.srcObject = stream;

			node.play().catch(() => {
				isOn = false;
			});

			return {
				update(newStream) {
					if (!newStream) return;

					if (node.srcObject !== newStream) {
						node.srcObject = newStream;

						node.play().catch(() => {
							isOn = false;
						});
					}
				},
				destroy() {
					try {
						node.pause();
						node.srcObject = null;
					} catch (e) {
						// Ignore cleanup errors
					}
				}
			};
		} catch (error) {
			return {};
		}
	}

	function didClickSwitchButton() {
		isOn = true;
		noMic = false;

		refreshTurnServer();
		connectToNetwork();

		if (navigator.mediaDevices) {
			navigator.mediaDevices
				.getUserMedia({
					audio: {
						echoCancellation: true,
						noiseSuppression: true,
						autoGainControl: true
					}
				})
				.then(async (stream) => {
					audioStream = stream;
					mediaStream = await createMediaStream(stream);

					if (mediaStream === null || mediaStream === undefined) {
						noMic = true;
					}
				})
				.catch(() => {
					noMic = true;
					journal.remember("tried to use the radio but couldn't access the microphone");
				});
		} else {
			noMic = true;
		}

		journal.remember("tried to find someone on the radio");
	}

	function connectToNetwork() {
		if (isOn === false) {
			return;
		}

		if (socketInitialized === false) {
			initializeSocket();
		}

		socket.open(() => {
			isOnline = true;
			if ($journey.location !== null && $journey.location !== undefined) {
				socket.send("client-online", {
					location: {
						latitude: $journey.location.latitude,
						longitude: $journey.location.longitude
					}
				});
			}
		});
	}

	function didPressTalkButton(event) {
		if (event.type === "touchstart") {
			event.preventDefault();
		}

		if (isOn === true && noMic === false) {
			talkie.start();
		}

		window.addEventListener("mouseup", didReleaseTalkButton);
		window.addEventListener("touchend", didReleaseTalkButton);
	}

	function didReleaseTalkButton() {
		talkie.end();

		window.removeEventListener("mouseup", didReleaseTalkButton);
		window.removeEventListener("touchend", didReleaseTalkButton);
	}

	function didPressKey(e) {
		if (e.target.nodeName === "TEXTAREA" || e.target.nodeName === "INPUT") {
			return;
		}

		if (!e.repeat && e.code === "Space" && isOn && noMic === false) {
			talkie.start();
		}
	}

	function didReleaseKey(e) {
		if (e.target.nodeName === "TEXTAREA" || e.target.nodeName === "INPUT") {
			return;
		}

		if (e.code === "Space" && isOn === true && noMic === false) {
			talkie.end();
		}
	}

	function addSocketListeners() {
		if (disposal.length > 0) {
			return; // Listeners already added
		}

		disposal = [
			socket.on("client-online", (message) => {
				if (message.from === ME) {
					return;
				}

				if (peers[message.from] !== undefined) {
					return;
				}

				createPeer(true, mediaStream, message.from);
				socket.send("client-invite", {}, message.from);
			}),

			socket.on("client-offline", (message) => {
				if (message.from === ME) {
					return;
				}

				destroyPeer(message.from);
			}),

			socket.on("client-invite", (message) => {
				if (message.from === ME) {
					return;
				}

				createPeer(false, mediaStream, message.from);
			}),

			socket.on("client-signal", (message) => {
				if (message.from === ME || message.signal === null || message.signal === undefined) {
					return;
				}

				if (peers[message.from] === undefined) {
					return;
				}

				try {
					peers[message.from].signal(message.signal);
				} catch (error) {
					handlePeerFailure(message.from, false);
				}
			})
		];
	}

	function removeSocketListeners() {
		disposal.forEach((off) => {
			off();
		});

		disposal = [];
	}

	function turnOff() {
		talkie.end();

		isOn = false;
		isOnline = false;
		wasTalking = false;

		// Clean up all peer connections
		Object.keys(peers).forEach(destroyPeer);
		peers = {};
		streams = {};
		connectionErrors = {};
		peerReconnectAttempts = {};

		// Stop all audio tracks
		if (audioStream !== null && audioStream !== undefined) {
			audioStream.getTracks().forEach((track) => {
				track.stop();
			});

			audioStream = undefined;
			mediaStream = undefined;
		}

		// Don't close audio context, just disconnect gain
		if (gain !== null && gain !== undefined) {
			try {
				gain.disconnect();
			} catch (e) {
				// Ignore disconnect errors
			}
			gain = undefined;
		}

		if (socketInitialized === true) {
			socket.broadcast("client-offline");
			removeSocketListeners();
		}
	}

	$: (() => {
		if (gain !== null && gain !== undefined && gain.gain !== null && gain.gain !== undefined) {
			try {
				gain.gain.value = $talkie.isTalking === true ? 1.0 : 0.0;
			} catch (e) {
				// Ignore gain errors if context is closed
			}
		}
	})();

	$: (() => {
		if ($talkie.isTalking) {
			wasTalking = true;

			if (overSound) {
				overSound.stop();
			}

			if (inSound) {
				inSound.play();
			}
		} else if (wasTalking) {
			wasTalking = false;

			if (overSound) {
				overSound.play();
			}

			if (inSound) {
				inSound.stop();
			}
		}
	})();

	function initializeSocket() {
		if (socketInitialized === true) {
			return;
		}

		socketInitialized = true;
		addSocketListeners();

		socket.onClose(() => {
			isOnline = false;
		});

		socket.onError(() => {
			isOnline = false;
		});
	}

	let lastLocationUpdateAt = 0;

	$: (() => {
		if (isOn === false || mediaStream === null || mediaStream === undefined || isOnline === false) {
			return;
		}

		const now = Date.now();
		if (now - lastLocationUpdateAt < 5_000) {
			return;
		}

		if ($location !== null && $location !== undefined) {
			lastLocationUpdateAt = now;
			socket.send("client-location", {
				location: $location
			});
		}
	})();

	onDestroy(() => {
		isDestroyed = true;
		turnOff();
		
		if (audioContext !== null && audioContext !== undefined) {
			try {
				audioContext.close();
			} catch (e) {
				// Ignore close errors
			}
			audioContext = undefined;
		}
	});
</script>

<svelte:window on:keydown={didPressKey} on:keyup={didReleaseKey} />

<div class="talkie">
	{#each Object.entries(streams) as [peerId, stream]}
		{#if stream}
			<audio use:srcObject={stream} bind:muted={$talkie.isTalking} data-peer-id={peerId} />
		{/if}
	{/each}

	<Display text={isOn ? displayText.toUpperCase() : "OFF"} rate={30} />

	<div class="controls">
		{#if isOn}
			<button
				on:mousedown={didPressTalkButton}
				on:touchstart={didPressTalkButton}
				class:pressed={$talkie.isTalking}
				class:disabled={noMic}
				disabled={noMic}>
				Talk
			</button>
			{#if noMic}
				<span>Mic not found</span>
			{:else if !isMobile}
				<span>or Spacebar</span>
			{/if}
		{:else}
			<button on:click={didClickSwitchButton}>Turn On</button>
		{/if}
		<div class="spacer" />
		<div class="indicator" class:talking={$talkie.isTalking} class:on={isOn} class:offline={!isOnline && isOn} />
	</div>

	{#if isOn && !isOnline}
		<p class="status-message">Connecting to network...</p>
	{/if}
</div>

<style>
	.talkie {
		padding: var(--spacing);
	}

	.controls {
		display: flex;
		position: relative;
		align-items: center;
		margin-top: var(--spacing);
	}

	span {
		position: relative;
		top: -1px;
		left: 6px;
		opacity: 0.5;
	}

	.spacer {
		flex-grow: 1;
	}

	button {
		appearance: none;
		margin: 0;
		border-top: var(--border-color-light) var(--chrome-depth) outset;
		border-right: var(--border-color-dark) var(--chrome-depth) outset;
		border-bottom: var(--border-color-dark) var(--chrome-depth) outset;
		border-left: var(--border-color-light) var(--chrome-depth) outset;
		background: var(--chrome-hsl);
		pointer-events: auto;
		color: black;
		text-shadow: none;
	}

	button.disabled {
		pointer-events: none;
		color: #999999;
		text-shadow: 0 1px rgba(255, 255, 255, 0.75);
	}

	button:not(.disabled).pressed,
	button:not(.disabled):active {
		filter: brightness(0.75);
		border-top: var(--border-color-dark) var(--chrome-depth) inset;
		border-right: var(--border-color-light) var(--chrome-depth) inset;
		border-bottom: var(--border-color-light) var(--chrome-depth) inset;
		border-left: var(--border-color-dark) var(--chrome-depth) inset;
	}

	.indicator {
		margin-left: 10px;
		border-top: var(--border-color-dark) var(--chrome-depth) inset;
		border-right: var(--border-color-light) var(--chrome-depth) inset;
		border-bottom: var(--border-color-light) var(--chrome-depth) inset;
		border-left: var(--border-color-dark) var(--chrome-depth) inset;
		border-radius: 10px;
		background: darkgray;
		width: 10px;
		height: 10px;
	}

	.indicator.on {
		background: red;
	}

	.indicator.talking {
		background: lime;
	}

	.indicator.offline {
		background: orange;
	}

	.status-message {
		font-size: 0.8em;
		color: orange;
		margin-top: 5px;
		text-align: center;
	}
</style>
